import Loader from 'components/Loader';
import ApiDowntimePage, { ApiDowntimeType } from 'pages/api-downtime/ApiDowntimePage';
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'store';
import { isServiceUnavailableStatus, setApiDown, setOnlineStatus } from 'store/reducers/apiStatus';

// Actuator health is the only management endpoint the backend exposes, and it is
// permitted for anonymous callers. An authenticated endpoint would answer 401 here,
// because this probe deliberately sends no credentials, and those 401s drown out the
// real authentication failures in monitoring.
const HEALTH_CHECK_URL = `${import.meta.env.VITE_API_URL}/actuator/health`;
const HEALTH_CHECK_TIMEOUT = 5000;
const RECOVERY_POLL_INTERVAL = 10000;
const INTERNET_CHECK_URL = 'https://fonts.googleapis.com';
// The SPA is served from this origin, so a live request to it proves the connection
// still works. Unlike the third-party probe it cannot be turned into a false negative
// by an ad blocker, a proxy allowlist or tracking protection, which is what keeps an
// unreachable API from being reported to the user as a broken connection.
const ORIGIN_CHECK_URL = `${window.location.origin}/favicon-32x32.png`;

const checkHealth = async (): Promise<boolean> => {
    try {
        const response = await fetch(HEALTH_CHECK_URL, {
            method: 'GET',
            cache: 'no-store',
            // Explicit, so the probe stays anonymous even where VITE_API_URL is same-origin
            credentials: 'omit',
            signal: AbortSignal.timeout(HEALTH_CHECK_TIMEOUT),
        });
        // Only the status is read, and an unread body keeps the request in flight until
        // the timeout signal aborts it.
        void response.body?.cancel().catch(() => {});
        // Reaching the host is not the same as being served by it: a maintenance window
        // answers 503, so that response has to count as down even though the request
        // completed. Every other response — 401 and 404 included — means the API is up.
        return !isServiceUnavailableStatus(response.status);
    } catch {
        return false;
    }
};

// Any HTTP response counts as reachable, so even a 404 from the probed host proves the
// connection works; only a network-level failure or the timeout rejects. Failure is
// left to reject rather than mapped to a boolean, so the Promise.any below settles on
// the first host that answers instead of waiting out a blocked or slow probe.
const probeReachable = async (url: string, init: RequestInit): Promise<void> => {
    const response = await fetch(url, {
        cache: 'no-store',
        signal: AbortSignal.timeout(HEALTH_CHECK_TIMEOUT),
        ...init,
    });
    // Unread like the health probe's, so released the same way.
    void response.body?.cancel().catch(() => {});
};

// Only called when the API is unreachable but the browser reports online. The probes
// run together and the first success wins: reaching any host other than the API means
// the connection is fine and the API is the part that is down. The user is only told
// their connection is at fault when every probe fails.
const checkInternet = async (): Promise<boolean> => {
    try {
        await Promise.any([
            probeReachable(ORIGIN_CHECK_URL, { method: 'GET' }),
            probeReachable(INTERNET_CHECK_URL, { method: 'HEAD', mode: 'no-cors' }),
        ]);
        return true;
    } catch {
        return false;
    }
};

const ApiStatusGuard = ({ children }: PropsWithChildren) => {
    const dispatch = useDispatch();
    const { isApiDown, isOnline } = useSelector((state) => state.apiStatus);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in JSX gate `if (!initialCheckDone) return <Loader />` (see below)
    const [initialCheckDone, setInitialCheckDone] = useState(false);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in JSX `hasInternet === false ? 'offline' : 'api-down'`
    const [hasInternet, setHasInternet] = useState<boolean | null>(null);
    const [hasMountedApp, setHasMountedApp] = useState(false);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // TODO(react-doctor: no-cascading-set-state): three independent state
    // updates (apiDown via redux, hasInternet, initialCheckDone) from a single
    // async chain — they represent distinct concerns. Refactoring would
    // entangle redux dispatch with local state.
    // Initial health check on mount
    useEffect(() => {
        checkHealth().then(async (reachable) => {
            if (!reachable) {
                dispatch(setApiDown(true));
                if (navigator.onLine) {
                    const internetAvailable = await checkInternet();
                    setHasInternet(internetAvailable);
                }
            }
            setInitialCheckDone(true);
        });
    }, [dispatch]);

    // Browser online/offline events
    useEffect(() => {
        const handleOnline = () => dispatch(setOnlineStatus(true));
        const handleOffline = () => dispatch(setOnlineStatus(false));

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [dispatch]);

    // TODO(react-doctor: no-cascading-set-state): poll-driven async updates;
    // distinct concerns (apiDown, hasInternet) — keeping cascade.
    // Recovery polling when API is down
    useEffect(() => {
        if (isApiDown && isOnline) {
            pollIntervalRef.current = setInterval(() => {
                void (async () => {
                    const reachable = await checkHealth();
                    if (reachable) {
                        dispatch(setApiDown(false));
                        setHasInternet(null);
                    } else {
                        const internetAvailable = await checkInternet();
                        setHasInternet(internetAvailable);
                    }
                })();
            }, RECOVERY_POLL_INTERVAL);
        }

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [isApiDown, isOnline, dispatch]);

    const handleRetry = useCallback(async () => {
        const reachable = await checkHealth();
        if (reachable) {
            dispatch(setApiDown(false));
            setHasInternet(null);
        } else if (isOnline) {
            const internetAvailable = await checkInternet();
            setHasInternet(internetAvailable);
        }
    }, [dispatch, isOnline]);

    if (!initialCheckDone) {
        return <Loader />;
    }

    let downtimeType: ApiDowntimeType | undefined;
    if (!isOnline) {
        downtimeType = 'offline';
    } else if (isApiDown) {
        downtimeType = hasInternet === false ? 'offline' : 'api-down';
    }

    // The app is first mounted only once the API answers: mounted into an outage, AuthProvider
    // reads the failed current-user request as an expired session and logs the user out.
    if (!downtimeType && !hasMountedApp) {
        setHasMountedApp(true);
    }

    // Once mounted, the app stays mounted under the overlay, so an outage or a dropped connection
    // does not discard what the user has typed into a form.
    return (
        <>
            {hasMountedApp && children}
            {downtimeType && (
                <ApiDowntimePage
                    type={downtimeType}
                    onRetry={() => {
                        void handleRetry();
                    }}
                />
            )}
        </>
    );
};

export default ApiStatusGuard;
